import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import * as actions from '~/actions';
import { clearSuccess } from '~/actions/success';
import { logCustomEvent } from '~/api/analytics';
import browserHistory from '~/browserHistory';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';
import { jobKeys } from '../jobKeys';

export interface IDeclineCandidatePayload {
  jobId: string;
  counterOfferId: string;
  cancellation_reason: string;
  candidateId: string;
  nextCandidateId?: string | null;
}

type TDeclineCandidateResponse = {
  isOnsiteInterview?: boolean;
  [key: string]: unknown;
};
type TDeclineCandidateError = AxiosError<{ message?: string; error?: string; title?: string }>;

export function useDeclineCandidateMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleDeclineError = (error: TDeclineCandidateError) => {
    if (error.response?.status === actions.NO_CARD_ERROR) {
      dispatch({ type: 'DECLINE_CANDIDATE_STOP_LOADING' });
      dispatch({ type: actions.SHOW_PAYMENT_MISSING_POPUP });
      return;
    }

    dispatch({ type: 'DECLINE_CANDIDATE_FAILURE', payload: error.response?.data });
  };

  return useMutation<TDeclineCandidateResponse, TDeclineCandidateError, IDeclineCandidatePayload>({
    mutationFn: async ({ jobId, counterOfferId, cancellation_reason }) => {
      return await apiMethods[apiMethodsConst.DECLINE_COUNTER_OFFER_REQUEST]({
        jobId,
        counterOfferId,
        cancellation_reason,
      });
    },
    onMutate: () => {
      dispatch({ type: 'DECLINE_CANDIDATE_REQUEST' });
    },
    onSuccess: async (response, variables) => {
      try {
        const { jobId, counterOfferId, candidateId, nextCandidateId } = variables;

        dispatch({ type: apiMethodsConst.DECLINE_COUNTER_OFFER_REQUEST, payload: response });
        void logCustomEvent('decline_candidate', {
          jobId,
          counterOfferId,
          candidateId,
        });

        queryClient.invalidateQueries({ queryKey: ['dashboard-jobs'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-jobs-info'] });
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
        queryClient.invalidateQueries({ queryKey: jobKeys.candidates(jobId) });

        dispatch({ type: 'DECLINE_CANDIDATE_SUCCESS', payload: { id: candidateId } });

        if (response?.isOnsiteInterview) {
          return;
        }

        if (nextCandidateId) {
          browserHistory.replace(`/dashboard/job/${jobId}/confirm/${nextCandidateId}`, {
            fromDeclineCandidate: true,
          });
        } else {
          browserHistory.replace(`/dashboard/job/${jobId}`);
        }

        dispatch(clearSuccess([actions.DECLINE_CANDIDATE]));
      } catch (error) {
        handleDeclineError(error as TDeclineCandidateError);
      }
    },
    onError: handleDeclineError,
  });
}
